import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Tenant } from "@/types";
import { AUTH_TENANTS } from "@/data/auth-fixtures";
import { checkSession, fetchExperienceContract, logout as apiLogout, setActiveOrganization, type SessionInfo } from "@/lib/api";
import { EXPERIENCE_CONTRACT_FIXTURE, type ExperienceContract } from "@/lib/experience-contract";

type AuthMethod = "passkey" | "totp" | "hardware" | "email";

interface AuthState {
  step: "loading" | "login" | "mfa" | "tenant-select" | "passkey-enrollment" | "session-expired" | "app";
  method: AuthMethod | null;
  selectedTenant: Tenant | null;
  processing: boolean;
  user: { name: string; email: string; initials: string; role: string } | null;
  session: SessionInfo | null;
  experience: ExperienceContract;
  selectOrganization: (organizationId: string) => Promise<void>;
  initSession: () => Promise<void>;
  goLogin: () => void;
  goMfa: (method: AuthMethod) => void;
  goTenantSelect: () => void;
  goEnrollment: () => void;
  goSessionExpired: () => void;
  selectTenant: (tenant: Tenant) => void;
  setProcessing: (v: boolean) => void;
  signOut: () => Promise<void>;
  differentUser: () => void;
}

function initialsFrom(email: string): string {
  const local = email.split("@")[0] || "";
  const parts = local.split(/[._-]/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      step: "loading",
      method: null,
      selectedTenant: null,
      processing: false,
      user: null,
      session: null,
      experience: EXPERIENCE_CONTRACT_FIXTURE,

      initSession: async () => {
        const session = await checkSession();
        if (session.authenticated) {
          const experience = await fetchExperienceContract();
          const email = session.email || "";
          const roleLabel = Array.isArray(session.roleNames) ? session.roleNames.join(", ") : "";
          set({
            step: "app",
            session,
            experience,
            user: {
              name: roleLabel || email,
              email,
              initials: initialsFrom(email),
              role: Array.isArray(session.roleNames) ? session.roleNames[0] || "wallet_operator" : "wallet_operator",
            },
          });
        } else {
          set({ step: "login", session: null, user: null, experience: EXPERIENCE_CONTRACT_FIXTURE });
        }
      },

      goLogin: () => set({ step: "login", processing: false }),
      goMfa: (method) => set({ step: "mfa", method, processing: false }),
      goTenantSelect: () => set({ step: "tenant-select", processing: false }),
      goEnrollment: () => set({ step: "passkey-enrollment", processing: false }),
      goSessionExpired: () => set({ step: "session-expired" }),
      selectTenant: (tenant) => set({ selectedTenant: tenant, step: "app", processing: false }),
      selectOrganization: async (organizationId) => {
        const current = get().experience;
        if (current.source === "api") {
          const experience = await setActiveOrganization(organizationId);
          set({ experience });
          return;
        }
        const match = current.memberships.find((membership) => membership.id === organizationId);
        if (!match) return;
        set({
          experience: {
            ...current,
            activeOrganization: {
              ...current.activeOrganization,
              id: match.id,
              label: match.label,
              tenantId: `${match.id.replace(/-org$/, "")}-tenant`,
              trustDomainId: `${match.id.replace(/-org$/, "")}-sandbox`,
            },
            memberships: current.memberships.map((membership) => ({
              ...membership,
              selected: membership.id === match.id,
            })),
          },
        });
      },
      setProcessing: (v) => set({ processing: v }),

      signOut: async () => {
        await apiLogout();
        set({ step: "login", selectedTenant: null, processing: false, method: null, session: null, user: null, experience: EXPERIENCE_CONTRACT_FIXTURE });
      },

      differentUser: () => set({ step: "login", selectedTenant: null, processing: false, method: null }),
    }),
    {
      name: "alloy-auth",
      partialize: (s) => ({ selectedTenant: s.selectedTenant, method: s.method }),
    },
  ),
);

export const TENANTS = AUTH_TENANTS;
